import { decryptString } from '../utils/crypto';

declare global {
  interface Window {
    __XF_API_BRIDGE_INSTALLED__?: boolean;
    __XF_DESKTOP__?: boolean;
  }
}

const BACKEND_ORIGIN = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:3000';

/** 读取 token 并自动解密（兼容 authService 的加密存储和旧版明文存储） */
function getAuthToken(): string | null {
  try {
    const raw = localStorage.getItem('xf_auth_token');
    if (!raw) return null;
    const decrypted = decryptString(raw);
    if (decrypted && decrypted.length > 0) return decrypted;
    if (raw.includes('.')) return raw;
    return null;
  } catch { return null; }
}
const API_RETRY_ATTEMPTS = 10;
const API_RETRY_BASE_MS = 250;

export function isTauriRuntime(): boolean {
  if (typeof window === 'undefined') return false;
  const win = window as any;
  return !!(win.__TAURI_INTERNALS__ || win.__TAURI_IPC__ || win.__TAURI__);
}

export function getBackendOrigin(): string {
  if (typeof window === 'undefined') return BACKEND_ORIGIN;
  // Desktop shell flag set by installApiBridge() — always route to local backend
  if ((window as any).__XF_DESKTOP__) return BACKEND_ORIGIN;
  // In Tauri production, WebView2 custom protocol can't reach the backend;
  // always route through the bundled Node.js server.
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  if (
    isTauriRuntime()
    || protocol === 'tauri:'
    || protocol === 'asset:'
    || hostname === 'tauri.localhost'
    || hostname.endsWith('.tauri.localhost')
  ) {
    return BACKEND_ORIGIN;
  }
  return window.location.origin;
}

export function getSocketOrigin(): string {
  return getBackendOrigin();
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function shouldRetry(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error || '');
  return /failed to fetch|networkerror|load failed|fetch/i.test(msg);
}

async function retryFetch(
  nativeFetch: typeof fetch,
  url: string,
  init: RequestInit,
  attempts: number,
  baseDelay: number,
): Promise<Response> {
  let lastError: unknown;
  for (let i = 0; i < attempts; i++) {
    try {
      return await nativeFetch(url, init);
    } catch (error) {
      lastError = error;
      if (i >= attempts - 1 || !shouldRetry(error)) throw error;
      const delay = Math.min(baseDelay + i * 350, 1500);
      console.debug(`[小枫OS Bridge] Retry ${i + 1}/${attempts} after ${delay}ms — backend may be starting…`);
      await sleep(delay);
    }
  }
  throw lastError instanceof Error
    ? lastError
    : new Error('Unable to reach the 小枫OS local server');
}

export function installApiBridge(): void {
  if (typeof window === 'undefined' || window.__XF_API_BRIDGE_INSTALLED__) return;

  const win = window as any;
  win.__XF_DESKTOP__ = true; // signal to getBackendOrigin that we're in a desktop shell

  const nativeFetch = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;

    // Never intercept Tauri IPC calls
    if (url.includes('ipc.localhost') || url.includes('tauri://')) {
      return nativeFetch(input, init);
    }

    // In Tauri, API paths need credentials for cross-origin (localhost vs 127.0.0.1)
    // But if same-origin, pass through untouched
    if (url.startsWith('/')) {
      const isDesktop = !!(win.__XF_DESKTOP__ || win.__TAURI_INTERNALS__ || win.__TAURI_IPC__ || win.__TAURI__);
      const needsCredentials = isDesktop && (url.startsWith('/api/') || url === '/api' || url.startsWith('/mcp/'));
      if (!needsCredentials) return nativeFetch(input, init);
      const patched: RequestInit = { ...init, credentials: 'include' };

      // WebView2 may not send httpOnly cookies — inject stored auth token as fallback
      try {
        const storedToken = getAuthToken();
        if (storedToken) {
          patched.headers = {
            ...(patched.headers as Record<string, string> || {}),
            'Authorization': `Bearer ${storedToken}`,
          };
        }
      } catch {}

      // In Tauri production, rewrite /api/* requests to the bundled Node.js backend
      // with automatic retry for slow backend startup
      return retryFetch(
        nativeFetch,
        BACKEND_ORIGIN + url,
        patched,
        API_RETRY_ATTEMPTS,
        API_RETRY_BASE_MS,
      );
    }

    return nativeFetch(input, init);
  };

  window.__XF_API_BRIDGE_INSTALLED__ = true;
}
